import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, ArrowLeft, DollarSign, Target, Calendar, Download, Eye, EyeOff } from 'lucide-react';
import { RetirementData, CalculationResults } from '../types';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './ResultsDashboard.css';

interface ResultsDashboardProps {
  data: RetirementData;
  results: CalculationResults;
  onRecalculate: () => void;
}

const ResultsDashboard: React.FC<ResultsDashboardProps> = ({ data, results, onRecalculate }) => {
  const [showFullInvestmentSchedule, setShowFullInvestmentSchedule] = useState(false);
  const [showFullWithdrawalSchedule, setShowFullWithdrawalSchedule] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Export to PDF functionality
  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const element = document.querySelector('.results-dashboard') as HTMLElement;
      if (!element) return;

      // Detect current theme
      const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
      const currentTheme = isDarkMode ? 'dark' : 'light';

      // Temporarily show all data for export
      const originalInvestmentShow = showFullInvestmentSchedule;
      const originalWithdrawalShow = showFullWithdrawalSchedule;
      setShowFullInvestmentSchedule(true);
      setShowFullWithdrawalSchedule(true);

      // Wait for state to update and render
      await new Promise(resolve => setTimeout(resolve, 500));

      // Create a clone of the element for PDF generation
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.width = '1400px';
      clone.style.padding = '30px';
      clone.style.fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      clone.style.fontSize = '14px';
      clone.style.lineHeight = '1.5';
      
      // Apply theme-aware PDF styles
      const backgroundColor = isDarkMode ? '#1f2937' : '#ffffff';
      const textColor = isDarkMode ? '#f9fafb' : '#111827';
      const borderColor = isDarkMode ? '#4b5563' : '#e2e8f0';
      const cardBg = isDarkMode ? '#374151' : '#f8fafc';
      const headerBg = isDarkMode ? '#4f46e5' : '#4f46e5';
      
      clone.style.backgroundColor = backgroundColor;
      clone.style.color = textColor;
      
      // Apply PDF-specific styles with theme support
      const style = document.createElement('style');
      style.textContent = `
        .pdf-export {
          font-size: 14px !important;
          line-height: 1.5 !important;
        }
        .pdf-export * {
          color: ${textColor} !important;
          border-color: ${borderColor} !important;
        }
        .pdf-export .dashboard-header {
          background-color: ${cardBg} !important;
          border: 1px solid ${borderColor} !important;
          box-shadow: none !important;
          margin-bottom: 20px !important;
          padding: 20px !important;
        }
        .pdf-export .dashboard-header h2 {
          font-size: 24px !important;
          font-weight: 700 !important;
          margin: 0 !important;
        }
        .pdf-export .back-btn,
        .pdf-export .export-btn {
          display: none !important;
        }
        .pdf-export .metric-card {
          background-color: ${cardBg} !important;
          border: 1px solid ${borderColor} !important;
          box-shadow: none !important;
          padding: 16px !important;
          margin-bottom: 12px !important;
        }
        .pdf-export .metric-value {
          font-size: 18px !important;
          font-weight: 700 !important;
        }
        .pdf-export .metric-content h3 {
          font-size: 12px !important;
          text-transform: uppercase !important;
          opacity: 0.8 !important;
        }
        .pdf-export .chart-container {
          background-color: ${cardBg} !important;
          border: 1px solid ${borderColor} !important;
          box-shadow: none !important;
          padding: 20px !important;
          margin-bottom: 20px !important;
          page-break-inside: avoid !important;
        }
        .pdf-export .chart-container h3 {
          font-size: 18px !important;
          font-weight: 600 !important;
          margin-bottom: 16px !important;
        }
        .pdf-export .table-container {
          background-color: ${cardBg} !important;
          border: 1px solid ${borderColor} !important;
          box-shadow: none !important;
          padding: 20px !important;
          margin-bottom: 20px !important;
          page-break-inside: avoid !important;
        }
        .pdf-export .table-container h3 {
          font-size: 18px !important;
          font-weight: 600 !important;
          margin-bottom: 16px !important;
        }
        .pdf-export .table-wrapper {
          border: 1px solid ${borderColor} !important;
          border-radius: 8px !important;
          overflow: visible !important;
        }
        .pdf-export .table-wrapper table {
          font-size: 12px !important;
          min-width: auto !important;
          width: 100% !important;
        }
        .pdf-export .table-wrapper th {
          background-color: ${headerBg} !important;
          color: #ffffff !important;
          padding: 12px 10px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
        }
        .pdf-export .table-wrapper td {
          padding: 10px !important;
          font-size: 11px !important;
          border-bottom: 1px solid ${borderColor} !important;
        }
        .pdf-export .table-wrapper tr:nth-child(even) {
          background-color: ${isDarkMode ? '#2d3748' : '#f8fafc'} !important;
        }
        .pdf-export .toggle-btn {
          display: none !important;
        }
        .pdf-export .table-note {
          display: none !important;
        }
        .pdf-export .metrics-grid {
          display: grid !important;
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 12px !important;
        }
        .pdf-export .charts-section {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 16px !important;
        }
        .pdf-export .tables-section {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 16px !important;
        }
        .pdf-export .recharts-wrapper {
          font-size: 10px !important;
        }
        .pdf-export .recharts-cartesian-axis-tick-value {
          font-size: 10px !important;
          fill: ${textColor} !important;
        }
        .pdf-export .recharts-legend-item-text {
          font-size: 10px !important;
        }
      `;
      
      clone.classList.add('pdf-export');
      document.head.appendChild(style);
      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        scale: 2.5,
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: backgroundColor,
        width: 1400,
        windowWidth: 1400,
        windowHeight: Math.max(clone.scrollHeight, 1000),
        onclone: (clonedDoc) => {
          // Ensure all elements are visible
          const clonedElement = clonedDoc.querySelector('.pdf-export') as HTMLElement;
          if (clonedElement) {
            clonedElement.style.transform = 'none';
            clonedElement.style.overflow = 'visible';
          }
        }
      });

      // Clean up
      document.body.removeChild(clone);
      document.head.removeChild(style);

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate dimensions to fit the page with margins
      const margin = 15;
      const availableWidth = pdfWidth - (2 * margin);
      const availableHeight = pdfHeight - (2 * margin + 30); // Extra space for header
      
      const ratio = Math.min(availableWidth / (imgWidth * 0.264583), availableHeight / (imgHeight * 0.264583));
      const scaledWidth = (imgWidth * 0.264583) * ratio;
      const scaledHeight = (imgHeight * 0.264583) * ratio;
      
      const x = (pdfWidth - scaledWidth) / 2;
      const y = margin + 25;

      // Add header with theme awareness
      pdf.setFontSize(22);
      pdf.setTextColor(79, 70, 229); // Primary color
      pdf.text('Retirement Planning Report', pdfWidth / 2, 15, { align: 'center' });
      
      // Add metadata
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      pdf.text(`Generated on: ${currentDate} • Theme: ${currentTheme}`, pdfWidth / 2, 22, { align: 'center' });

      // Check if content fits on one page
      if (scaledHeight > availableHeight) {
        // Multi-page handling
        const pagesNeeded = Math.ceil(scaledHeight / availableHeight);
        
        for (let i = 0; i < pagesNeeded; i++) {
          if (i > 0) {
            pdf.addPage();
            // Add page header
            pdf.setFontSize(16);
            pdf.setTextColor(79, 70, 229);
            pdf.text('Retirement Planning Report (continued)', pdfWidth / 2, 15, { align: 'center' });
          }
          
          const sourceY = i * (imgHeight / pagesNeeded);
          const sourceHeight = Math.min(imgHeight / pagesNeeded, imgHeight - sourceY);
          const targetHeight = Math.min(availableHeight, (sourceHeight * 0.264583) * ratio);
          
          // Create a canvas for this page section
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = imgWidth;
          pageCanvas.height = sourceHeight;
          const pageCtx = pageCanvas.getContext('2d');
          
          if (pageCtx) {
            pageCtx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
            const pageImgData = pageCanvas.toDataURL('image/png', 1.0);
            pdf.addImage(pageImgData, 'PNG', x, i === 0 ? y : margin + 20, scaledWidth, targetHeight);
          }
        }
      } else {
        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      }

      // Add footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.text(`Page ${i} of ${pageCount}`, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
      }

      pdf.save(`retirement-plan-${currentTheme}-${new Date().toISOString().split('T')[0]}.pdf`);

      // Restore original state
      setShowFullInvestmentSchedule(originalInvestmentShow);
      setShowFullWithdrawalSchedule(originalWithdrawalShow);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Prepare chart data
  const investmentChartData = results.monthlyInvestments.map(inv => ({
    age: inv.age,
    monthlyInvestment: inv.monthlyAmount,
    corpus: inv.yearEndCorpus,
    inflationAdjusted: inv.inflationAdjustedAmount
  }));

  const withdrawalChartData = results.monthlyWithdrawals.map(withdrawal => ({
    age: withdrawal.age,
    monthlyWithdrawal: withdrawal.monthlyWithdrawal,
    remainingCorpus: withdrawal.remainingCorpus
  }));

  // Calculate advanced investments total
  const totalAdvancedInvestments = data.advancedInvestments.reduce((sum, inv) => sum + inv.amount, 0);
  
  const summaryData = [
    { name: 'Regular Investments', value: results.totalInvestmentNeeded, color: '#4361ee' },
    { name: 'Advanced Investments', value: totalAdvancedInvestments, color: '#9333ea' },
    { name: 'Returns', value: results.totalReturns, color: '#2ec4b6' },
    { name: 'Current Savings', value: data.savingsTillNow, color: '#ff9f1c' }
  ].filter(item => item.value > 0); // Only show items with value > 0

  const keyMetrics = [
    {
      title: 'Required Corpus',
      value: formatCurrency(results.requiredCorpus),
      icon: Target,
      color: 'primary'
    },
    {
      title: 'Monthly Investment Needed',
      value: formatCurrency(results.monthlyInvestments[0]?.monthlyAmount || 0),
      icon: DollarSign,
      color: 'secondary'
    },
    {
      title: 'Investment Period',
      value: `${data.retirementAge - data.currentAge} years`,
      icon: Calendar,
      color: 'accent'
    },
    {
      title: 'Expected Returns',
      value: formatCurrency(results.totalReturns),
      icon: TrendingUp,
      color: 'success'
    }
  ];

  return (
    <div className="results-dashboard">
      <motion.div 
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <button className="back-btn" onClick={onRecalculate}>
          <ArrowLeft size={20} />
          Recalculate
        </button>
        <h2>Your Retirement Plan</h2>
        <button className="export-btn" onClick={exportToPDF} disabled={isExporting}>
          <Download size={20} />
          {isExporting ? 'Exporting...' : 'Export PDF'}
        </button>
      </motion.div>

      {/* Key Metrics Cards */}
      <motion.div 
        className="metrics-grid"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {keyMetrics.map((metric, index) => (
          <motion.div
            key={metric.title}
            className={`metric-card ${metric.color}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 * index }}
          >
            <div className="metric-icon">
              <metric.icon size={24} />
            </div>
            <div className="metric-content">
              <h3>{metric.title}</h3>
              <p className="metric-value">{metric.value}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Investment Growth Chart */}
        <motion.div 
          className="chart-container"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>Investment Growth Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={investmentChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <Tooltip 
                formatter={(value: number, name: string) => [formatCurrency(value), name]}
                labelFormatter={(label) => `Age: ${label}`}
              />
              <Line 
                type="monotone" 
                dataKey="corpus" 
                stroke="#4361ee" 
                strokeWidth={3}
                name="Total Corpus"
              />
              <Line 
                type="monotone" 
                dataKey="monthlyInvestment" 
                stroke="#2ec4b6" 
                strokeWidth={2}
                name="Monthly Investment"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Monthly Investment Bar Chart */}
        <motion.div 
          className="chart-container"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3>Monthly Investment Requirements</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={investmentChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <Tooltip 
                formatter={(value: number, name: string) => [formatCurrency(value), name]}
                labelFormatter={(label) => `Age: ${label}`}
              />
              <Bar dataKey="monthlyInvestment" fill="#4361ee" name="Nominal Amount" />
              <Bar dataKey="inflationAdjusted" fill="#2ec4b6" name="Today's Value" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Portfolio Composition Pie Chart */}
        <motion.div 
          className="chart-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3>Portfolio Composition</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={summaryData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {summaryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Withdrawal Chart */}
        {withdrawalChartData.length > 0 && (
          <motion.div 
            className="chart-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <h3>Retirement Withdrawals</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={withdrawalChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="age" />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip 
                  formatter={(value: number, name: string) => [formatCurrency(value), name]}
                  labelFormatter={(label) => `Age: ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="remainingCorpus" 
                  stroke="#ff9f1c" 
                  strokeWidth={3}
                  name="Remaining Corpus"
                />
                <Line 
                  type="monotone" 
                  dataKey="monthlyWithdrawal" 
                  stroke="#e71d36" 
                  strokeWidth={2}
                  name="Monthly Withdrawal"
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>

      {/* Tables Section */}
      <div className="tables-section">
        <motion.div 
          className="table-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="table-header">
            <h3>Investment Schedule</h3>
            {results.monthlyInvestments.length > 10 && (
              <button 
                className="toggle-btn"
                onClick={() => setShowFullInvestmentSchedule(!showFullInvestmentSchedule)}
              >
                {showFullInvestmentSchedule ? <EyeOff size={16} /> : <Eye size={16} />}
                {showFullInvestmentSchedule ? 'Show Less' : 'Show All'}
              </button>
            )}
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Age</th>
                  <th>Monthly Investment</th>
                  <th>Today's Value</th>
                  <th>Year-End Corpus</th>
                </tr>
              </thead>
              <tbody>
                {(showFullInvestmentSchedule ? results.monthlyInvestments : results.monthlyInvestments.slice(0, 10)).map((investment, index) => (
                  <tr key={index}>
                    <td data-label="Age">{investment.age}</td>
                    <td data-label="Monthly Investment">{formatCurrency(investment.monthlyAmount)}</td>
                    <td data-label="Today's Value">{formatCurrency(investment.inflationAdjustedAmount)}</td>
                    <td data-label="Year-End Corpus">{formatCurrency(investment.yearEndCorpus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!showFullInvestmentSchedule && results.monthlyInvestments.length > 10 && (
              <p className="table-note">Showing first 10 years. Click "Show All" to see full schedule.</p>
            )}
          </div>
        </motion.div>

        {withdrawalChartData.length > 0 && (
          <motion.div 
            className="table-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <div className="table-header">
              <h3>Withdrawal Schedule</h3>
              {results.monthlyWithdrawals.length > 10 && (
                <button 
                  className="toggle-btn"
                  onClick={() => setShowFullWithdrawalSchedule(!showFullWithdrawalSchedule)}
                >
                  {showFullWithdrawalSchedule ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showFullWithdrawalSchedule ? 'Show Less' : 'Show All'}
                </button>
              )}
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Age</th>
                    <th>Monthly Withdrawal</th>
                    <th>Remaining Corpus</th>
                  </tr>
                </thead>
                <tbody>
                  {(showFullWithdrawalSchedule ? results.monthlyWithdrawals : results.monthlyWithdrawals.slice(0, 10)).map((withdrawal, index) => (
                    <tr key={index}>
                      <td data-label="Age">{withdrawal.age}</td>
                      <td data-label="Monthly Withdrawal">{formatCurrency(withdrawal.monthlyWithdrawal)}</td>
                      <td data-label="Remaining Corpus">{formatCurrency(withdrawal.remainingCorpus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!showFullWithdrawalSchedule && results.monthlyWithdrawals.length > 10 && (
                <p className="table-note">Showing first 10 years. Click "Show All" to see full schedule.</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Advanced Investments Summary */}
        {data.advancedInvestments.length > 0 && (
          <motion.div
            className="table-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <div className="table-header">
              <h3>Advanced Investments Summary</h3>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Investment</th>
                    <th>Principal Amount</th>
                    <th>Interest Rate</th>
                    <th>Start Year</th>
                    <th>Duration</th>
                    <th>Growth Years</th>
                    <th>Maturity Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.advancedInvestments.map((investment, index) => {
                    const yearsToRetirement = data.retirementAge - data.currentAge;
                    // Skip if investment starts after retirement
                    if (investment.startYear >= yearsToRetirement) {
                      return (
                        <tr key={investment.id} style={{ opacity: 0.6 }}>
                          <td data-label="Investment">{investment.name || `Investment ${index + 1}`}</td>
                          <td data-label="Principal">{formatCurrency(investment.amount)}</td>
                          <td data-label="Rate">{investment.interestRate}%</td>
                          <td data-label="Start">{investment.startYear === 0 ? 'Immediate' : `Year ${investment.startYear}`}</td>
                          <td data-label="Duration">{investment.timeYears} years</td>
                          <td data-label="Growth">0 years (starts after retirement)</td>
                          <td data-label="Maturity Value">{formatCurrency(0)}</td>
                        </tr>
                      );
                    }

                    // Calculate actual growth years
                    const investmentEndYear = investment.startYear + investment.timeYears;
                    const actualGrowthYears = Math.min(investmentEndYear, yearsToRetirement) - investment.startYear;
                    const maturityValue = actualGrowthYears > 0
                      ? investment.amount * Math.pow(1 + investment.interestRate / 100, actualGrowthYears)
                      : investment.amount;
                    
                    return (
                      <tr key={investment.id}>
                        <td data-label="Investment">{investment.name || `Investment ${index + 1}`}</td>
                        <td data-label="Principal">{formatCurrency(investment.amount)}</td>
                        <td data-label="Rate">{investment.interestRate}%</td>
                        <td data-label="Start">{investment.startYear === 0 ? 'Immediate' : `Year ${investment.startYear}`}</td>
                        <td data-label="Duration">{investment.timeYears} years</td>
                        <td data-label="Growth">{actualGrowthYears} years</td>
                        <td data-label="Maturity Value">{formatCurrency(maturityValue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 'bold', borderTop: '2px solid var(--border-color)' }}>
                    <td data-label="Investment">Total</td>
                    <td data-label="Principal">{formatCurrency(totalAdvancedInvestments)}</td>
                    <td data-label="Rate">-</td>
                    <td data-label="Start">-</td>
                    <td data-label="Duration">-</td>
                    <td data-label="Growth">-</td>
                    <td data-label="Maturity Value">{formatCurrency(data.advancedInvestments.reduce((sum, inv) => {
                      const yearsToRetirement = data.retirementAge - data.currentAge;
                      if (inv.startYear >= yearsToRetirement) return sum;
                      
                      const investmentEndYear = inv.startYear + inv.timeYears;
                      const actualGrowthYears = Math.min(investmentEndYear, yearsToRetirement) - inv.startYear;
                      
                      return actualGrowthYears > 0
                        ? sum + inv.amount * Math.pow(1 + inv.interestRate / 100, actualGrowthYears)
                        : sum + inv.amount;
                    }, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ResultsDashboard; 